import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { WizardStep, WizardStepId } from './addAssetWizardSteps';

/**
 * SIL-1812: bridge between the Add Asset page (owns the wizard state, local
 * to `SubmitAssetPage`) and the setup rail in `AppShell` (renders the steps
 * nested under "Add an asset"). The page publishes `{ steps, interactive,
 * onStepClick }`; the rail reads it. Both hooks tolerate a missing provider
 * (isolated component tests) so neither side needs the other to exist.
 */
export interface WizardRailState {
  steps: WizardStep[];
  /** false while the success panel is showing - no step is clickable then. */
  interactive: boolean;
  onStepClick: (stepId: WizardStepId) => void;
}

// Two contexts so the publishing page subscribes only to the (stable) setter
// and does not re-render every time the state it just published changes.
const WizardRailStateContext = createContext<WizardRailState | null>(null);
const WizardRailSetContext = createContext<((next: WizardRailState | null) => void) | null>(null);

export function AddAssetWizardRailProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WizardRailState | null>(null);
  return (
    <WizardRailSetContext.Provider value={setState}>
      <WizardRailStateContext.Provider value={state}>{children}</WizardRailStateContext.Provider>
    </WizardRailSetContext.Provider>
  );
}

/** Rail side: the published wizard state, or null when the page is not mounted. */
export function useAddAssetWizardRail(): WizardRailState | null {
  return useContext(WizardRailStateContext);
}

/** Page side: publish the wizard state while mounted; cleared on unmount. */
export function usePublishAddAssetWizardRail(next: WizardRailState): void {
  const setState = useContext(WizardRailSetContext);
  const { steps, interactive, onStepClick } = next;
  useEffect(() => {
    setState?.({ steps, interactive, onStepClick });
  }, [setState, steps, interactive, onStepClick]);
  useEffect(() => {
    return () => setState?.(null);
  }, [setState]);
}

/** One-line hint under each wizard step in the setup rail. */
export const WIZARD_STEP_HINTS: Record<WizardStepId, string> = {
  method: 'Choose how to add your hardware',
  build: 'We make a probe for your account',
  run: 'Run it on the machine you want to add',
  identify: 'Tell us what the hardware is',
  condition: 'Condition and where it came from',
  confirm: 'Check the hardware we found',
};
